import type {
  AdoptionAssumptions,
  Clinic,
  CostItem,
  ProjectionModel,
  Scenario,
} from "./types";

const cloneAdoption = (adoption: AdoptionAssumptions): AdoptionAssumptions => ({
  ...adoption,
  connectionMix: adoption.connectionMix.map((item) => ({ ...item })),
  marketingMix: adoption.marketingMix.map((item) => ({ ...item })),
});

const DEFAULT_ADOPTION: AdoptionAssumptions = {
  pctAvanzado: 0.4,
  pctConexion: 0.5,
  pctMarketing: 0,
  pctPanel: 0,
  connectionMix: [
    { packId: "wa_200", weight: 0.3 },
    { packId: "wa_500", weight: 0.5 },
    { packId: "wa_800", weight: 0 },
    { packId: "wa_1000", weight: 0.15 },
    { packId: "wa_1200", weight: 0 },
    { packId: "wa_1500", weight: 0.05 },
  ],
  marketingMix: [
    { packId: "mkt_200", weight: 0.4 },
    { packId: "mkt_500", weight: 0.3 },
    { packId: "mkt_1000", weight: 0.2 },
    { packId: "mkt_2500", weight: 0.1 },
    { packId: "mkt_5000", weight: 0 },
  ],
  whatsappUsageRate: 0.6,
  emailUsageRate: 0.7,
  includeDebt: true,
  panelAvgClinics: 3,
};

/** Cartera del Excel: [profesionales, consultorios, WA del último mes informado]. */
const CLINIC_SEED: Array<[number, number, number]> = [
  [2, 1, 556],
  [3, 1, 574],
  [1, 1, 0],
  [1, 1, 174],
  [1, 1, 205],
  [1, 1, 885],
  [1, 1, 119],
  [1, 1, 255],
  [1, 1, 240],
  [1, 3, 410],
  [1, 1, 1110],
  [1, 1, 255],
  [1, 1, 754],
  [2, 2, 155],
  [10, 8, 4937],
  [2, 2, 682],
  [1, 1, 56],
  [1, 2, 245],
  [1, 2, 772],
  [6, 3, 6539],
  [1, 1, 0],
  [1, 1, 236],
  [1, 1, 535],
  [1, 1, 350],
  [2, 1, 651],
  [1, 1, 404],
  [3, 1, 740],
  [1, 1, 906],
  [1, 1, 484],
  [1, 1, 371],
  [1, 1, 178],
  [1, 1, 659],
  [1, 3, 1228],
  [2, 2, 859],
  [1, 1, 287],
  [2, 1, 393],
  [1, 1, 504],
  [1, 1, 92],
];

const defaultClinics = (): Clinic[] =>
  CLINIC_SEED.map(([professionals, consultorios, whatsappHint], index) => ({
    id: `clinic_${String(index + 1).padStart(2, "0")}`,
    name: `Clínica ${String(index + 1).padStart(2, "0")}`,
    professionals,
    consultorios,
    enabled: true,
    whatsappHint,
  }));

const defaultCosts = (): CostItem[] => [
  { id: "infra_apps", name: "PROD - APPS", amount: 67277.8, currency: "ARS", frequency: "monthly", category: "Infra", kind: "operating", enabled: true },
  { id: "infra_sql", name: "PROD - SQL", amount: 158222, currency: "ARS", frequency: "monthly", category: "Infra", kind: "operating", enabled: true },
  { id: "test_apps", name: "apps-testing", amount: 99377, currency: "ARS", frequency: "monthly", category: "Testing", kind: "operating", enabled: true },
  { id: "test_redis", name: "Redis testing", amount: 10269.4, currency: "ARS", frequency: "monthly", category: "Testing", kind: "operating", enabled: true },
  { id: "test_v2", name: "TEST - COOFIT V2", amount: 46037.4, currency: "ARS", frequency: "monthly", category: "Testing", kind: "operating", enabled: true },
  { id: "infra_v2", name: "PROD - COOFIT V2", amount: 28175.2, currency: "ARS", frequency: "monthly", category: "Infra", kind: "operating", enabled: true },
  { id: "infra_redis", name: "Redis PROD", amount: 10269.4, currency: "ARS", frequency: "monthly", category: "Infra", kind: "operating", enabled: true },
  { id: "conn_sendgrid", name: "SendGrid", amount: 20, currency: "USD", frequency: "monthly", category: "Conexión", kind: "operating", enabled: true },
  { id: "gen_mail", name: "Mails corporativos", amount: 12, currency: "USD", frequency: "monthly", category: "General", kind: "operating", enabled: true },
  { id: "ops_dev", name: "Desarrollador actual", amount: 1000, currency: "USD", frequency: "monthly", category: "Operación", kind: "operating", enabled: true },
  { id: "ops_devs", name: "Otros desarrolladores", amount: 600000, currency: "ARS", frequency: "monthly", category: "Operación", kind: "operating", enabled: true },
  { id: "debt_dev", name: "Deuda desarrollador (30 meses)", amount: 1000, currency: "USD", frequency: "monthly", category: "Deuda temporal", kind: "debt", enabled: true },
  { id: "app_apple", name: "Apple Developer", amount: 100, currency: "USD", frequency: "annual", category: "App", kind: "operating", enabled: true },
  { id: "app_google", name: "Google Play", amount: 25, currency: "USD", frequency: "annual", category: "App", kind: "operating", enabled: true },
  { id: "infra_ssl", name: "SSL Sectigo", amount: 36000, currency: "ARS", frequency: "annual", category: "Infra", kind: "operating", enabled: true },
  { id: "infra_domain", name: "Dominio sistema-coofit.com", amount: 46900, currency: "ARS", frequency: "annual", category: "Infra", kind: "operating", enabled: true },
  { id: "email_domain_com", name: "Dominio coofit-email.com", amount: 46900, currency: "ARS", frequency: "annual", category: "Email", kind: "operating", enabled: true },
  { id: "email_domain_online", name: "Dominio coofit-email.online", amount: 52580, currency: "ARS", frequency: "annual", category: "Email", kind: "operating", enabled: true },
  { id: "email_domain_store", name: "Dominio coofit-email.store", amount: 63320, currency: "ARS", frequency: "annual", category: "Email", kind: "operating", enabled: true },
  { id: "email_privacy", name: "Privacidad dominio", amount: 10659, currency: "ARS", frequency: "annual", category: "Email", kind: "operating", enabled: true },
  { id: "email_security", name: "Seguridad Premium", amount: 3373, currency: "ARS", frequency: "annual", category: "Email", kind: "operating", enabled: true },
  { id: "email_renew", name: "Seguro renovación", amount: 6064, currency: "ARS", frequency: "annual", category: "Email", kind: "operating", enabled: true },
];

const defaultScenarios = (): Scenario[] => [
  {
    id: "conservador",
    name: "Conservador",
    adoption: {
      ...cloneAdoption(DEFAULT_ADOPTION),
      pctAvanzado: 0.25,
      pctConexion: 0.3,
      connectionMix: [
        { packId: "wa_200", weight: 0.45 },
        { packId: "wa_500", weight: 0.45 },
        { packId: "wa_800", weight: 0 },
        { packId: "wa_1000", weight: 0.08 },
        { packId: "wa_1200", weight: 0 },
        { packId: "wa_1500", weight: 0.02 },
      ],
      whatsappUsageRate: 0.8,
    },
  },
  {
    id: "base",
    name: "Base",
    adoption: cloneAdoption(DEFAULT_ADOPTION),
  },
  {
    id: "fuerte",
    name: "Fuerte",
    adoption: {
      ...cloneAdoption(DEFAULT_ADOPTION),
      pctAvanzado: 0.55,
      pctConexion: 0.65,
      connectionMix: [
        { packId: "wa_200", weight: 0.2 },
        { packId: "wa_500", weight: 0.5 },
        { packId: "wa_800", weight: 0 },
        { packId: "wa_1000", weight: 0.2 },
        { packId: "wa_1200", weight: 0 },
        { packId: "wa_1500", weight: 0.1 },
      ],
      whatsappUsageRate: 0.5,
    },
  },
  {
    id: "alta",
    name: "Alta adopción",
    adoption: {
      ...cloneAdoption(DEFAULT_ADOPTION),
      pctAvanzado: 0.7,
      pctConexion: 0.8,
      connectionMix: [
        { packId: "wa_200", weight: 0.15 },
        { packId: "wa_500", weight: 0.45 },
        { packId: "wa_800", weight: 0 },
        { packId: "wa_1000", weight: 0.25 },
        { packId: "wa_1200", weight: 0 },
        { packId: "wa_1500", weight: 0.15 },
      ],
      whatsappUsageRate: 0.4,
    },
  },
];

export function createDefaultProjectionModel(): ProjectionModel {
  const clinics = defaultClinics();
  const avgProfessionals =
    clinics.reduce((sum, clinic) => sum + clinic.professionals, 0) / clinics.length;
  const avgConsultorios =
    clinics.reduce((sum, clinic) => sum + clinic.consultorios, 0) / clinics.length;

  return {
    version: 1,
    assumptions: {
      usdArs: 1500,
      metaUsdPerWhatsapp: 0.026,
      sendgridEmailsPerMonth: 50000,
      sendgridUsdPerMonth: 20,
    },
    costs: defaultCosts(),
    plans: {
      basePrice: 70000,
      avanzadoPrice: 115000,
      extraProfessional: 15000,
      extraConsultorio: 15000,
    },
    connectionPacks: [
      { id: "wa_200", quota: 200, price: 35000, enabled: true },
      { id: "wa_500", quota: 500, price: 60000, enabled: true },
      { id: "wa_800", quota: 800, price: 80000, enabled: true },
      { id: "wa_1000", quota: 1000, price: 95000, enabled: true },
      { id: "wa_1200", quota: 1200, price: 110000, enabled: true },
      { id: "wa_1500", quota: 1500, price: 130000, enabled: true },
    ],
    marketingPacks: [
      { id: "mkt_200", name: "Marketing 200", emailsPerMonth: 200, price: 30000, enabled: true },
      { id: "mkt_500", name: "Marketing 500", emailsPerMonth: 500, price: 40000, enabled: true },
      { id: "mkt_1000", name: "Marketing 1.000", emailsPerMonth: 1000, price: 50000, enabled: true },
      { id: "mkt_2500", name: "Marketing 2.500", emailsPerMonth: 2500, price: 70000, enabled: true },
      { id: "mkt_5000", name: "Marketing 5.000", emailsPerMonth: 5000, price: 90000, enabled: true },
    ],
    panelClinicas: {
      basePrice: 100000,
      includedClinics: 3,
      extraClinicPrice: 25000,
    },
    portfolioMode: "detailed",
    clinics,
    synthetic: {
      clinicCount: clinics.length,
      avgProfessionals: Math.round(avgProfessionals * 10) / 10,
      avgConsultorios: Math.round(avgConsultorios * 10) / 10,
    },
    adoption: cloneAdoption(DEFAULT_ADOPTION),
    growth: {
      months: 12,
      newClinicsPerMonth: 0,
      churnMonthlyPct: 0,
      avgNewProfessionals: 1,
      avgNewConsultorios: 1,
    },
    scenarios: defaultScenarios(),
  };
}
